import { useState } from 'react';
import React from 'react';
import 'quill/dist/quill.snow.css';
import ReactQuill from 'react-quill';
import { HtmlEditor } from './HtmlEditor';
import { Flex } from '../compositeComponents/FlexHelpers';
import { InputMessage } from '../InputMessage';
import { SaveCancelOverlayButtonRow } from './_components/SaveCancelOverlayButtonRow';

// errorMessage is always shown if set to a value
// If onSave returns true, the editor will exit edit mode
// Else it will remain in edit mode

type HtmlEditorWithAutoSaveProps =
    {
        id: string,
        value: string,
        onChange?:(content: string) => void,
        // If onSave returns true, the editor will exit edit mode
        onSave?: (content: string) => Promise<boolean>, 
        onCancel?: () => void,
        onBlur?: () => void,
        errorMessage?: string,
        placeholder?: string,
        readonly?: boolean,
        ariaLabel?: string,
        debugPanel?: boolean,
    };

export function HtmlEditorWithAutoSave (props: HtmlEditorWithAutoSaveProps): JSX.Element
{
    const [editorContent, setEditorContent] = useState(props.value);
    const [inEdit,  setInEdit] = useState(false);

    const ref = React.useRef<ReactQuill>(null);
    const cancelButtonRef = React.useRef<HTMLButtonElement>(null);

    React.useEffect(() =>
    {
        setEditorContent(props.value);
        if(inEdit){
            setInEdit(false);
        }
    }, [props.value]);

    React.useEffect(() =>
    {
        if(props.errorMessage && !inEdit){
            setInEdit(true);
        }
    }, [props.errorMessage]);

    React.useEffect(() =>
    {
        if(ref && ref.current){
            if(inEdit){
                ref.current.focus();
            }
            else
            {
                ref.current.blur();
            }
        }
    }, [inEdit]);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    function handleEditorFocus()
    {
        setInEdit(true);
    }

    function handleEditorChange(content: string)
    {
        setEditorContent(content);
        props.onChange?.(content);
    }
    
    function handleEditorCancel()
    {
        setEditorContent(props.value);
        setInEdit(false);
        props.onCancel?.();
    }

    async function handleEditorSave()
    {
        const success = await props.onSave?.(editorContent);
        if(success)
            setInEdit(false);
    }

    async function handleEditorBlur(e: React.FocusEvent<HTMLDivElement, Element>)
    {
        const isCancel = e.relatedTarget === cancelButtonRef.current;
        if(isCancel){
            handleEditorCancel();
        }
        else {
            handleEditorSave();
        }
    }

    const changed = props.value !== editorContent;

    return (
        <Flex flexDirection="column">
            <div style={{ position: 'relative' }}>
                <HtmlEditor
                    id={props.id}
                    value={editorContent}
                    onChange={handleEditorChange}
                    onBlur={handleEditorBlur}
                    onFocus={handleEditorFocus}
                    placeholder={props.placeholder}
                    readonly={props.readonly}
                    debugPanel={props.debugPanel}
                    innerRef={ref}
                    inErrorState={!!props.errorMessage}
                />
                <SaveCancelOverlayButtonRow
                    show={inEdit && !props.readonly && changed}
                    cancelButtonRef={cancelButtonRef}
                    onSave={handleEditorCancel}
                    onCancel={handleEditorCancel}
                />
            </div>
            <InputMessage inErrorState={!!props.errorMessage}>
                {props.errorMessage}
            </InputMessage>
        </Flex>
    );
}